<template>
  <div class="static-sites-container">
    <div class="page-header">
      <h2>前端网页托管</h2>
    </div>

    <!-- Tab 切换 -->
    <el-card v-if="site">
      <el-tabs v-model="activeTab">
        <!-- 站点信息 Tab -->
        <el-tab-pane label="站点信息" name="info">
          <div class="site-info-section">
            <div class="info-header">
              <h3>站点配置</h3>
              <el-button v-if="$canWrite()" size="small" icon="el-icon-edit" @click="openEditDialog">编辑配置</el-button>
            </div>

            <el-descriptions :column="2" border>
              <el-descriptions-item label="访问地址">
                <a :href="site.public_url" target="_blank" class="site-url">
                  {{ site.public_url }}
                  <i class="el-icon-link"></i>
                </a>
              </el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="site.status === 'active' ? 'success' : 'info'" size="small">
                  {{ site.status === 'active' ? '运行中' : '已停用' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="自定义域名">
                {{ site.domain || '未配置' }}
              </el-descriptions-item>
              <el-descriptions-item label="首页文件">
                {{ site.index_file }}
              </el-descriptions-item>
              <el-descriptions-item label="创建时间">
                {{ site.created_at }}
              </el-descriptions-item>
              <el-descriptions-item label="更新时间">
                {{ site.updated_at }}
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </el-tab-pane>

        <!-- 文件管理 Tab -->
        <el-tab-pane label="文件管理" name="files">
          <div class="file-manager-wrapper">
            <div class="file-manager-content">
              <!-- 面包屑导航 -->
              <el-breadcrumb separator="/" class="breadcrumb-nav">
                <el-breadcrumb-item>
                  <a @click="navigateToPath('')">根目录</a>
                </el-breadcrumb-item>
                <el-breadcrumb-item v-for="(part, index) in pathParts" :key="index">
                  <a @click="navigateToPath(getPathUpTo(index))">{{ part }}</a>
                </el-breadcrumb-item>
              </el-breadcrumb>

              <div class="file-actions">
                <el-dropdown v-if="$canWrite()" @command="handleCreateCommand" trigger="click">
                  <el-button size="small" type="primary">
                    新建<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="folder" icon="el-icon-folder-add">新建文件夹</el-dropdown-item>
                    <el-dropdown-item command="file" icon="el-icon-document-add">新建文件</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                
                <el-upload
                  v-if="$canWrite()"
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :data="uploadData"
                  :on-success="handleUploadSuccess"
                  :on-error="handleUploadError"
                  :show-file-list="false"
                  multiple
                >
                  <el-button size="small" type="primary" icon="el-icon-upload">上传文件</el-button>
                </el-upload>
                
                <el-upload
                  v-if="$canWrite()"
                  :action="uploadZipUrl"
                  :headers="uploadHeaders"
                  :data="uploadData"
                  :on-success="handleZipUploadSuccess"
                  :on-error="handleUploadError"
                  :show-file-list="false"
                  accept=".zip"
                >
                  <el-button size="small" type="success" icon="el-icon-upload2">上传 ZIP</el-button>
                </el-upload>
                
                <el-button size="small" icon="el-icon-refresh" @click="fetchFiles">刷新</el-button>
              </div>

              <div class="table-wrapper">
                <el-table :data="paginatedFiles" border style="width: 100%;">
                  <el-table-column prop="name" label="文件名" min-width="200">
                    <template slot-scope="scope">
                      <span v-if="scope.row.type === 'directory'" 
                            @click="navigateToPath(scope.row.path)" 
                            style="cursor: pointer; color: #409EFF;">
                        <i class="el-icon-folder"></i> {{ scope.row.name }}
                      </span>
                      <span v-else>
                        <i class="el-icon-document"></i> {{ scope.row.name }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="type" label="类型" width="100">
                    <template slot-scope="scope">
                      <el-tag :type="scope.row.type === 'directory' ? 'info' : 'success'" size="small">
                        {{ scope.row.type === 'directory' ? '目录' : '文件' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="size" label="大小" width="120">
                    <template slot-scope="scope">
                      {{ scope.row.type === 'file' ? formatFileSize(scope.row.size) : '-' }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="updated_at" label="更新时间" width="180"></el-table-column>
                  <el-table-column label="操作" width="180" fixed="right">
                    <template slot-scope="scope">
                      <el-button v-if="$canWrite() && isEditableFile(scope.row)" type="text" size="small" @click="openEditFileDialog(scope.row)">编辑</el-button>
                      <el-button v-if="$canWrite()" type="text" size="small" @click="openRenameDialog(scope.row)">重命名</el-button>
                      <el-button v-if="$canWrite()" type="text" size="small" style="color: #F56C6C;" @click="deleteFile(scope.row)">删除</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>

            <!-- 分页 -->
            <div class="pagination-footer">
              <div class="footer-left"></div>
              <el-pagination
                @current-change="handleFilePageChange"
                :current-page="filePagination.currentPage"
                :page-size="filePagination.pageSize"
                layout="total, prev, pager, next, jumper"
                :total="files.length"
              ></el-pagination>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 加载中 -->
    <div v-if="loading && !site" class="loading-container">
      <el-spinner></el-spinner>
      <p>正在初始化站点...</p>
    </div>

    <!-- 编辑站点配置对话框 -->
    <el-dialog
      title="编辑站点配置"
      :visible.sync="editDialogVisible"
      width="500px"
    >
      <el-form ref="siteForm" :model="siteForm" :rules="siteRules" label-width="100px">
        <el-form-item label="自定义域名" prop="domain">
          <el-input v-model="siteForm.domain" placeholder="例如: www.example.com"></el-input>
          <span class="form-tip">可选，需要配置 DNS 解析</span>
        </el-form-item>
        <el-form-item label="首页文件" prop="index_file">
          <el-input v-model="siteForm.index_file" placeholder="默认: index.html"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="siteForm.status">
            <el-radio label="active">运行中</el-radio>
            <el-radio label="inactive">已停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSiteUpdate" :loading="loading">保存</el-button>
      </div>
    </el-dialog>

    <!-- 创建文件夹对话框 -->
    <el-dialog
      title="新建文件夹"
      :visible.sync="createFolderDialogVisible"
      width="400px"
    >
      <el-form ref="folderForm" :model="folderForm" :rules="folderRules" label-width="80px">
        <el-form-item label="文件夹名" prop="name">
          <el-input v-model="folderForm.name" placeholder="请输入文件夹名称"></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="createFolderDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateFolder" :loading="loading">创建</el-button>
      </div>
    </el-dialog>

    <!-- 新建文件对话框 -->
    <el-dialog
      title="新建文件"
      :visible.sync="createFileDialogVisible"
      width="400px"
    >
      <el-form ref="fileForm" :model="fileForm" :rules="fileRules" label-width="80px">
        <el-form-item label="文件名" prop="name">
          <el-input v-model="fileForm.name" placeholder="例如: index.html, style.css, app.js"></el-input>
          <span class="form-tip">文件扩展名会根据文件名自动识别</span>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="createFileDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateFile" :loading="loading">创建</el-button>
      </div>
    </el-dialog>

    <!-- 重命名对话框 -->
    <el-dialog
      title="重命名"
      :visible.sync="renameDialogVisible"
      width="400px"
    >
      <el-form ref="renameForm" :model="renameForm" :rules="renameRules" label-width="80px">
        <el-form-item label="新名称" prop="name">
          <el-input v-model="renameForm.name" placeholder="请输入新名称"></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="renameDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleRename" :loading="loading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 编辑文件抽屉 -->
    <el-drawer
      :title="'编辑文件 - ' + (editFileForm.name || '')"
      :visible.sync="editFileDialogVisible"
      direction="rtl"
      size="60%"
      :before-close="handleDrawerClose"
    >
      <div class="edit-file-container">
        <codemirror
          v-model="editFileForm.content"
          :options="cmOptions"
          class="code-editor"
        ></codemirror>
        
        <div class="drawer-footer">
          <span style="color: #909399; font-size: 12px; margin-right: auto;">提示: Ctrl+S / Cmd+S 快速保存</span>
          <el-button @click="editFileDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSaveFile" :loading="loading">保存</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/monokai.css'

// 导入常用语言模式
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/css/css.js'
import 'codemirror/mode/htmlmixed/htmlmixed.js'
import 'codemirror/mode/xml/xml.js'
import 'codemirror/mode/php/php.js'
import 'codemirror/mode/python/python.js'
import 'codemirror/mode/markdown/markdown.js'
import 'codemirror/mode/vue/vue.js'

export default {
  name: 'StaticSites',
  components: {
    codemirror
  },
  data() {
    return {
      site: null,
      files: [],
      currentPath: '',
      activeTab: 'files',
      editDialogVisible: false,
      createFolderDialogVisible: false,
      createFileDialogVisible: false,
      renameDialogVisible: false,
      editFileDialogVisible: false,
      loading: false,
      filePagination: {
        currentPage: 1,
        pageSize: 20
      },
      siteForm: {
        domain: '',
        index_file: 'index.html',
        status: 'active'
      },
      siteRules: {
        index_file: [
          { required: true, message: '请输入首页文件名', trigger: 'blur' }
        ]
      },
      folderForm: {
        name: ''
      },
      folderRules: {
        name: [
          { required: true, message: '请输入文件夹名称', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_-]+$/, message: '只能包含字母、数字、下划线和横线', trigger: 'blur' }
        ]
      },
      fileForm: {
        name: ''
      },
      fileRules: {
        name: [
          { required: true, message: '请输入文件名', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_.-]+$/, message: '只能包含字母、数字、下划线、横线和点', trigger: 'blur' }
        ]
      },
      renameForm: {
        name: '',
        oldPath: ''
      },
      renameRules: {
        name: [
          { required: true, message: '请输入新名称', trigger: 'blur' }
        ]
      },
      editFileForm: {
        name: '',
        path: '',
        content: ''
      },
      cmOptions: {
        tabSize: 2,
        mode: 'text/plain',
        theme: 'monokai',
        lineNumbers: true,
        line: true,
        lineWrapping: false,
        styleActiveLine: true,
        matchBrackets: true,
        autoCloseBrackets: true,
        indentWithTabs: false,
        smartIndent: true,
        extraKeys: {
          'Ctrl-S': () => {
            this.handleSaveFile()
          },
          'Cmd-S': () => {
            this.handleSaveFile()
          }
        }
      }
    }
  },
  computed: {
    projectId() {
      return this.$route.params.id
    },
    pathParts() {
      return this.currentPath ? this.currentPath.split('/').filter(p => p) : []
    },
    paginatedFiles() {
      const start = (this.filePagination.currentPage - 1) * this.filePagination.pageSize
      const end = start + this.filePagination.pageSize
      return this.files.slice(start, end)
    },
    uploadUrl() {
      return this.site ? `/api/projects/${this.projectId}/sites/${this.site.id}/upload` : ''
    },
    uploadZipUrl() {
      return this.site ? `/api/projects/${this.projectId}/sites/${this.site.id}/upload-zip` : ''
    },
    uploadHeaders() {
      return {
        'Authorization': `Bearer ${Cookies.get('token')}`
      }
    },
    uploadData() {
      return {
        path: this.currentPath
      }
    }
  },
  created() {
    this.initSite()
  },
  methods: {
    async initSite() {
      this.loading = true
      try {
        // 获取项目的站点列表
        const response = await this.$axios.get(`/projects/${this.projectId}/sites`)
        
        if (response.data.length > 0) {
          // 如果已有站点，使用第一个
          this.site = response.data[0]
        } else {
          // 如果没有站点，自动创建一个默认站点
          await this.createDefaultSite()
        }
        
        // 加载文件列表
        if (this.site) {
          this.fetchFiles()
        }
      } catch (error) {
        this.$message.error('初始化站点失败')
      } finally {
        this.loading = false
      }
    },
    
    async createDefaultSite() {
      try {
        const response = await this.$axios.post(`/projects/${this.projectId}/sites`, {
          name: 'default',
          index_file: 'index.html'
        })
        this.site = response.data
        this.$message.success('站点初始化成功')
      } catch (error) {
        this.$message.error('创建站点失败')
      }
    },
    
    openEditDialog() {
      this.siteForm = {
        domain: this.site.domain || '',
        index_file: this.site.index_file,
        status: this.site.status
      }
      this.editDialogVisible = true
    },
    
    async handleSiteUpdate() {
      this.$refs.siteForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            const response = await this.$axios.put(
              `/projects/${this.projectId}/sites/${this.site.id}`,
              this.siteForm
            )
            this.site = { ...this.site, ...response.data }
            this.$message.success('配置更新成功')
            this.editDialogVisible = false
          } catch (error) {
            this.$message.error(error.response?.data?.error || '更新失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    async fetchFiles() {
      if (!this.site) return
      
      try {
        const params = this.currentPath ? { path: this.currentPath } : {}
        const response = await this.$axios.get(`/projects/${this.projectId}/sites/${this.site.id}/files`, { params })
        this.files = response.data
        this.filePagination.currentPage = 1
      } catch (error) {
        this.$message.error('获取文件列表失败')
      }
    },
    
    navigateToPath(path) {
      this.currentPath = path
      this.fetchFiles()
    },
    
    getPathUpTo(index) {
      return this.pathParts.slice(0, index + 1).join('/')
    },
    
    showCreateFolderDialog() {
      this.folderForm.name = ''
      this.createFolderDialogVisible = true
    },
    
    handleCreateCommand(command) {
      if (command === 'folder') {
        this.showCreateFolderDialog()
      } else if (command === 'file') {
        this.showCreateFileDialog()
      }
    },
    
    showCreateFileDialog() {
      this.fileForm.name = ''
      this.createFileDialogVisible = true
    },
    
    async handleCreateFile() {
      this.$refs.fileForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            const filePath = this.currentPath 
              ? `${this.currentPath}/${this.fileForm.name}`
              : this.fileForm.name
            
            // 根据文件扩展名确定初始内容
            const ext = this.fileForm.name.split('.').pop().toLowerCase()
            const initialContent = this.getInitialFileContent(ext, this.fileForm.name)
            
            // 创建文件（通过更新文件内容接口，传入空内容即可创建）
            await this.$axios.put(`/projects/${this.projectId}/sites/${this.site.id}/file-content`, {
              path: filePath,
              content: initialContent
            })
            
            this.$message.success('文件创建成功')
            this.createFileDialogVisible = false
            this.fetchFiles()
          } catch (error) {
            this.$message.error(error.response?.data?.error || '创建失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    getInitialFileContent(ext, filename) {
      // 根据文件扩展名返回初始模板内容
      const nameWithoutExt = filename.replace('.' + ext, '')
      
      if (ext === 'html') {
        return '<!DOCTYPE html>\n<html lang="zh-CN">\n<head>\n    <meta charset="UTF-8">\n    <meta name="viewport" content="width=device-width, initial-scale=1.0">\n    <title>Document</title>\n</head>\n<body>\n    \n</body>\n</html>'
      } else if (ext === 'css') {
        return '/* ' + filename + ' */\n\n'
      } else if (ext === 'js') {
        return '// ' + filename + '\n\n'
      } else if (ext === 'json') {
        return '{\n  \n}'
      } else if (ext === 'md') {
        return '# ' + nameWithoutExt + '\n\n'
      } else if (ext === 'vue') {
        const lines = [
          '<template>',
          '  <div>',
          '    ',
          '  </div>',
          '</template>',
          '',
          '<' + 'script>',
          'export default {',
          '  name: \'' + nameWithoutExt + '\',',
          '  data() {',
          '    return {',
          '      ',
          '    }',
          '  }',
          '}',
          '</' + 'script>',
          '',
          '<style scoped>',
          '',
          '</style>'
        ]
        return lines.join('\n')
      } else if (ext === 'php') {
        return '<?php\n\n'
      } else if (ext === 'py') {
        return '# ' + filename + '\n\n'
      } else if (ext === 'xml') {
        return '<?xml version="1.0" encoding="UTF-8"?>\n\n'
      }
      
      return ''
    },
    
    async handleCreateFolder() {
      this.$refs.folderForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            const folderPath = this.currentPath 
              ? `${this.currentPath}/${this.folderForm.name}`
              : this.folderForm.name
            
            await this.$axios.post(`/projects/${this.projectId}/sites/${this.site.id}/create-folder`, {
              path: folderPath
            })
            
            this.$message.success('文件夹创建成功')
            this.createFolderDialogVisible = false
            this.fetchFiles()
          } catch (error) {
            this.$message.error(error.response?.data?.error || '创建失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    handleFilePageChange(page) {
      this.filePagination.currentPage = page
    },
    
    handleUploadSuccess() {
      this.$message.success('文件上传成功')
      this.fetchFiles()
    },
    
    handleZipUploadSuccess(response) {
      this.$message.success(`ZIP 解压成功，共 ${response.files_count} 个文件`)
      this.fetchFiles()
    },
    
    handleUploadError() {
      this.$message.error('上传失败')
    },
    
    async deleteFile(file) {
      try {
        await this.$confirm(`确定要删除 ${file.name} 吗？`, '提示', {
          type: 'warning'
        })
        
        await this.$axios.delete(`/projects/${this.projectId}/sites/${this.site.id}/files`, {
          data: { path: file.path }
        })
        this.$message.success('删除成功')
        this.fetchFiles()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    
    isEditableFile(file) {
      if (file.type === 'directory') return false
      
      const editableExtensions = [
        'html', 'htm', 'css', 'js', 'json', 'xml', 'txt', 'md',
        'php', 'py', 'java', 'c', 'cpp', 'h', 'cs', 'go', 'rs',
        'vue', 'jsx', 'tsx', 'ts', 'scss', 'sass', 'less',
        'yml', 'yaml', 'toml', 'ini', 'conf', 'sh', 'bat'
      ]
      
      const ext = file.name.split('.').pop().toLowerCase()
      return editableExtensions.includes(ext)
    },
    
    openRenameDialog(file) {
      this.renameForm = {
        name: file.name,
        oldPath: file.path
      }
      this.renameDialogVisible = true
    },
    
    async handleRename() {
      this.$refs.renameForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            const newPath = this.currentPath 
              ? `${this.currentPath}/${this.renameForm.name}`
              : this.renameForm.name
            
            await this.$axios.put(`/projects/${this.projectId}/sites/${this.site.id}/rename`, {
              old_path: this.renameForm.oldPath,
              new_path: newPath
            })
            
            this.$message.success('重命名成功')
            this.renameDialogVisible = false
            this.fetchFiles()
          } catch (error) {
            this.$message.error(error.response?.data?.error || '重命名失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    
    async openEditFileDialog(file) {
      this.loading = true
      try {
        const response = await this.$axios.get(`/projects/${this.projectId}/sites/${this.site.id}/file-content`, {
          params: { path: file.path }
        })
        
        this.editFileForm = {
          name: file.name,
          path: file.path,
          content: response.data.content
        }
        
        // 根据文件扩展名设置语法高亮模式
        const ext = file.name.split('.').pop().toLowerCase()
        this.cmOptions.mode = this.getCodeMirrorMode(ext)
        
        this.editFileDialogVisible = true
      } catch (error) {
        this.$message.error('读取文件失败')
      } finally {
        this.loading = false
      }
    },
    
    getCodeMirrorMode(ext) {
      const modeMap = {
        'js': 'text/javascript',
        'jsx': 'text/jsx',
        'ts': 'text/typescript',
        'tsx': 'text/typescript-jsx',
        'json': 'application/json',
        'html': 'text/html',
        'htm': 'text/html',
        'xml': 'application/xml',
        'css': 'text/css',
        'scss': 'text/x-scss',
        'sass': 'text/x-sass',
        'less': 'text/x-less',
        'php': 'application/x-httpd-php',
        'py': 'text/x-python',
        'md': 'text/x-markdown',
        'vue': 'text/x-vue',
        'java': 'text/x-java',
        'c': 'text/x-csrc',
        'cpp': 'text/x-c++src',
        'h': 'text/x-csrc',
        'cs': 'text/x-csharp',
        'go': 'text/x-go',
        'rs': 'text/x-rustsrc',
        'sh': 'text/x-sh',
        'yml': 'text/x-yaml',
        'yaml': 'text/x-yaml'
      }
      
      return modeMap[ext] || 'text/plain'
    },
    
    async handleSaveFile() {
      this.loading = true
      try {
        await this.$axios.put(`/projects/${this.projectId}/sites/${this.site.id}/file-content`, {
          path: this.editFileForm.path,
          content: this.editFileForm.content
        })
        
        this.$message.success('保存成功')
        this.editFileDialogVisible = false
        this.fetchFiles()
      } catch (error) {
        this.$message.error(error.response?.data?.error || '保存失败')
      } finally {
        this.loading = false
      }
    },
    
    handleDrawerClose(done) {
      this.$confirm('确认关闭？未保存的内容将丢失')
        .then(() => {
          done()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.static-sites-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.info-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.site-info-section {
  padding: 20px 0;
}

.file-manager-wrapper {
  height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
}

.file-manager-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 20px 0 0 0;
}

.breadcrumb-nav {
  flex-shrink: 0;
  margin-bottom: 16px;
}

.file-actions {
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.pagination-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #e4e7ed;
  background: white;
}

.pagination-footer .footer-left {
  flex: 1;
}

.site-info-card {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.file-manager-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.site-url {
  color: #409EFF;
  text-decoration: none;
}

.site-url:hover {
  text-decoration: underline;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  display: block;
  margin-top: 4px;
}

.loading-container {
  text-align: center;
  padding: 100px 0;
  color: #909399;
}

.edit-file-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 20px;
}

.code-editor {
  flex: 1;
  margin-bottom: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.code-editor >>> .CodeMirror {
  height: 100%;
  font-family: 'Courier New', Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
}

.code-editor >>> .CodeMirror-gutters {
  border-right: 1px solid #ddd;
}

.drawer-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}
</style>
